<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-container" style="width: 50%;">
                <div class="modal-header">
                    <slot name="header">
                        <h3 class="form-header">Theatre Check In</h3>
                        <button @click="closeModal" class="red-close-button">
                            <i class="pi pi-times" style='padding: 0'></i>
                        </button>
                    </slot>
                </div>
                <div class="modal-body">
                    <slot name="body">
                        <form class="action-modal">
                            <div class="form-section-container" style="display: flex; flex-direction: column; width: 100%;">
                                
                                <div v-if="!selectedCheckout" style="margin-bottom: 20px;">
                                    <p style="font-weight: bold; margin-bottom: 10px;">Search Pending Checkouts</p>
                                    <div style="display: flex;">
                                        <Dropdown
                                            v-model="searchSelection"
                                            :options="pendingCheckouts"
                                            optionLabel="display_name"
                                            style="flex: 1; margin-right: 10px;"
                                            placeholder="Select a pending checkout..."
                                        />
                                        <button type="button" @click="selectCheckout" class="blue-button" style="padding: 10px;">Select</button>
                                    </div>
                                    <div v-if="loadingCheckouts" style="text-align: center; padding: 20px;">
                                        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
                                    </div>
                                    <div v-if="pendingCheckouts.length === 0 && !loadingCheckouts" style="padding: 10px; color: gray;">
                                        No pending checkouts found.
                                    </div>
                                </div>

                                <div v-if="selectedCheckout" style="display: flex; gap: 20px;">
                                    <div style="flex: 1;">
                                        <p style="font-weight: bold; margin-bottom: 10px;">Checkout Details</p>
                                        <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px;">
                                            <p v-if="selectedCheckout.mission_number"><b>Mission Number:</b> {{ selectedCheckout.mission_number }}</p>
                                            <p><b>Date:</b> {{ new Date(selectedCheckout.created_at).toLocaleString() }}</p>
                                            <p><b>{{ $resident({ capitalize: true }) }}:</b> {{ selectedCheckout.patient ? selectedCheckout.patient.personal_information.firstname + ' ' + selectedCheckout.patient.personal_information.lastname : 'N/A' }}</p>
                                            <p><b>Anesthetist:</b> {{ selectedCheckout.clinician ? selectedCheckout.clinician.personal_information.firstname + ' ' + selectedCheckout.clinician.personal_information.lastname : 'N/A' }}</p>
                                            <p v-if="selectedCheckout.notes"><b>Checkout Notes:</b> {{ selectedCheckout.notes }}</p>
                                            <button type="button" @click="clearSelection" class="regular-button error-button" style="margin-top: 10px; padding: 3px 12px; font-size: 0.85em;">Change Checkout</button>
                                        </div>

                                        <div v-if="hasDiscard">
                                            <p style="font-weight: bold; margin-top: 20px; margin-bottom: 5px;">How was it destroyed?</p>
                                            <input type="text" v-model="discardMethod" style="width: 100%; border-radius: 4px; padding: 8px;" placeholder="e.g. expelled into a sharps container" />
                                            <p style="font-size: 0.8em; color: gray; margin-top: 4px;">Recorded against each discard, along with this location and your name.</p>
                                        </div>

                                        <p style="font-weight: bold; margin-top: 20px; margin-bottom: 5px;">Notes</p>
                                        <textarea v-model="notes" rows="4" style="width: 100%; border-radius: 4px; padding: 8px;" placeholder="Optional check-in notes..."></textarea>
                                    </div>

                                    <div style="flex: 1;">
                                        <p style="font-weight: bold; margin-bottom: 10px;">Account For Medications</p>
                                        <table style="width: 100%; border-collapse: collapse;">
                                            <tr>
                                                <th style="text-align: left; padding-bottom: 5px;">Drug</th>
                                                <th style="text-align: center; width: 45px;">Out</th>
                                                <th style="text-align: center; width: 60px;">Return</th>
                                                <th v-if="canAdminister" style="text-align: center; width: 60px;">Given</th>
                                                <th v-if="canDiscard" style="text-align: center; width: 60px;">Discard</th>
                                            </tr>
                                            <tr v-for="(drug, index) in checkinDrugs" :key="index" style="border-bottom: 1px solid #eee;">
                                                <td style="padding: 10px 0; max-width: 250px; cursor: pointer;"
                                                    @click="expandedDrugs.includes(index) ? expandedDrugs.splice(expandedDrugs.indexOf(index), 1) : expandedDrugs.push(index)"
                                                    :title="expandedDrugs.includes(index) ? 'Click to collapse' : 'Click to expand: ' + formatDrugName(drug.stock_item)">
                                                    <div :style="expandedDrugs.includes(index) ? { whiteSpace: 'normal' } : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }">
                                                        {{ formatDrugName(drug.stock_item) }}
                                                    </div>
                                                    <div v-if="outstanding(drug) !== 0" style="font-size: 0.8em; color: #b26b00; margin-top: 4px;">
                                                        {{ outstanding(drug) > 0 ? outstanding(drug) + ' still out' : 'Over the amount checked out' }}
                                                    </div>
                                                </td>
                                                <td style="text-align: center;">{{ drug.remaining }}</td>
                                                <td style="text-align: center;">
                                                    <input type="number" v-model="drug.checkin_qty" min="0" :max="drug.remaining" style="width: 55px; text-align: center;" />
                                                </td>
                                                <td v-if="canAdminister" style="text-align: center;">
                                                    <input type="number" v-model="drug.administered_qty" min="0" :max="drug.remaining" style="width: 55px; text-align: center;" />
                                                </td>
                                                <td v-if="canDiscard" style="text-align: center;">
                                                    <input type="number" v-model="drug.discard_qty" min="0" :max="drug.remaining" style="width: 55px; text-align: center;" />
                                                </td>
                                            </tr>
                                        </table>
                                        <p style="font-size: 0.85em; color: gray; margin-top: 10px;">
                                            This closes the checkout, so account for everything still out - what came back, what was given and what was destroyed. Each is recorded against the theatre's own register.
                                        </p>
                                        <p v-if="!canAdminister" style="font-size: 0.85em; color: gray; margin-top: 10px;">
                                            No {{ $resident() }} is recorded against this checkout, so an administration cannot be recorded here.
                                        </p>
                                    </div>
                                </div>
                                
                            </div>
                        </form>
                    </slot>
                </div>
                <p style="color: red; font-weight: 500; font-size: 1em; text-align: center; margin-top: 10px;" v-if="errorMessage">{{ errorMessage }}</p>
                <div class="modal-footer" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
                    <slot name="footer">
                        <p class="regular-button error-button" style="color: white; margin-right: 10px;" @click="closeModal">Cancel</p>
                        <p class="regular-button green-button" @click="submitCheckin" v-if="selectedCheckout">Check In</p>
                    </slot>
                </div>
            </div>
        </div>
        <SignatureModal v-if="displaySignatureModal" :payload="realSubmit" @isSuccessful="isSuccessful" @close="close" :saveContext="saveContext" endpoint="/theatre-checkin" />
    </div>
</template>

<script>
import SignatureModal from '@/components/modals/SignatureModal.vue';
import axios from 'axios';
import { drugsRequireDoubleSig } from '@/services/doubleSignature';

export default {
    components: {
        SignatureModal
    },
    props: {
        preselectedCheckoutId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            loadingCheckouts: false,
            pendingCheckouts: [],
            searchSelection: null,
            selectedCheckout: null,
            checkinDrugs: [],
            expandedDrugs: [],
            notes: '',
            discardMethod: '',
            
            errorMessage: '',
            displaySignatureModal: false,
            realSubmit: null,
            saveContext: null
        }
    },
    computed: {
        requiresDoubleSig() {
            const facility = this.$store.getters.getCurrentLocation?.facility;
            return drugsRequireDoubleSig(this.checkinDrugs.filter(d => this.accountedFor(d) > 0), facility);
        },
        // An administration has to belong to someone. A theatre checkout can be made without one,
        // and kraken refuses an administered amount in that case, so the column is hidden instead.
        canAdminister() {
            return !!(this.selectedCheckout && this.selectedCheckout.patient);
        },
        canDiscard() {
            return true;
        },
        hasDiscard() {
            return this.checkinDrugs.some(d => Number(d.discard_qty || 0) > 0);
        },
        // The check in closes the checkout, so it has to account for everything still out - what
        // came back, what was given and what was destroyed. Anything left over would sit in the
        // theatre's register belonging to a checkout nobody will open again.
        unaccountedDrugs() {
            return this.checkinDrugs.filter(d => this.outstanding(d) !== 0);
        },
    },
    mounted() {
        this.fetchPendingCheckouts();
    },
    methods: {
        formatDrugName(stockItem) {
            if (!stockItem || !stockItem.alias) return 'Unknown Medication';
            const alias = stockItem.alias;
            const generic = alias.generic_name || (alias.generic_drug && alias.generic_drug.name);
            return generic ? `(${generic}) ${alias.name}` : alias.name;
        },
        closeModal() {
            this.$emit('close', true);
        },
        close(value) {
            if (value) {
                this.displaySignatureModal = false;
            }
        },
        fetchPendingCheckouts() {
            this.loadingCheckouts = true;
            // Fetch checkouts with status 'open'
            axios.post('/theatre-checkout-search', { status: 'open' }, this.$store.state.header).then(res => {
                this.pendingCheckouts = res.data.data.map(checkout => {
                    let dateStr = new Date(checkout.created_at).toLocaleString();
                    let pName = checkout.patient ? checkout.patient.personal_information.firstname + ' ' + checkout.patient.personal_information.lastname : 'No ' + this.$resident({ capitalize: true });
                    let missionSuffix = checkout.mission_number ? ` (Mission ${checkout.mission_number})` : '';
                    checkout.display_name = `${dateStr} - ${pName}${missionSuffix}`;
                    return checkout;
                });
                this.loadingCheckouts = false;

                if (this.preselectedCheckoutId && !this.selectedCheckout) {
                    const match = this.pendingCheckouts.find(c => c.uuid === this.preselectedCheckoutId);
                    if (match) {
                        this.searchSelection = match;
                        this.selectCheckout();
                    }
                }
            }).catch(err => {
                this.loadingCheckouts = false;
                console.error("Failed to fetch pending checkouts", err);
            });
        },
        // What is still out on this drug. Kraken works it out - the two register models account for
        // it differently - and quantity never changes, so it is not the figure to show.
        remainingFor(drug) {
            return Number(drug.outstanding || 0);
        },
        accountedFor(drug) {
            return Number(drug.checkin_qty || 0) + Number(drug.administered_qty || 0) + Number(drug.discard_qty || 0);
        },
        // What stays out with the clinician after this check in. It is not a shortfall - the supply
        // entry already accounted for it and the checkout stays open holding it.
        outstanding(drug) {
            return drug.remaining - this.accountedFor(drug);
        },
        selectCheckout() {
            if (this.searchSelection) {
                this.selectedCheckout = this.searchSelection;
                this.checkinDrugs = [];
                JSON.parse(JSON.stringify(this.selectedCheckout.drugs)).forEach(d => {
                    const remaining = this.remainingFor(d);
                    if (remaining > 0) {
                        d.remaining = remaining;
                        d.checkin_qty = 0;
                        d.administered_qty = 0;
                        d.discard_qty = 0;
                        this.checkinDrugs.push(d);
                    }
                });
                this.errorMessage = '';
            } else {
                this.errorMessage = "Please select a checkout to proceed.";
            }
        },
        clearSelection() {
            this.selectedCheckout = null;
            this.checkinDrugs = [];
            this.searchSelection = null;
        },
        submitCheckin() {
            let invalidQty = false;
            let overRemaining = false;

            // Nothing to check in means nothing to account for, so every check below would pass and
            // the checkout would be closed on an empty list. It is empty when no drug has an amount
            // still out, which is not a check in the user can make.
            if (!this.checkinDrugs.length) {
                this.errorMessage = "This checkout has nothing left to check in.";
                return;
            }

            const payloadDrugs = this.checkinDrugs.map(d => {
                const checkin = Number(d.checkin_qty || 0);
                const administered = Number(d.administered_qty || 0);
                const discard = Number(d.discard_qty || 0);

                if (checkin < 0 || administered < 0 || discard < 0) invalidQty = true;
                if (checkin + administered + discard > d.remaining) overRemaining = true;

                return {
                    drug: d.uuid,
                    checkin: checkin,
                    administered: administered,
                    discard: discard,
                    tags: d.stock_item?.alias?.tags || []
                };
            });

            if (invalidQty) {
                this.errorMessage = "Quantities cannot be negative.";
                return;
            }

            if (overRemaining) {
                this.errorMessage = "Returned, given and discarded together cannot exceed the amount still checked out.";
                return;
            }

            if (this.hasDiscard && !this.discardMethod.trim()) {
                this.errorMessage = "Say how the discarded medication was destroyed.";
                return;
            }

            // The check in closes the checkout, so everything still out has to be accounted for
            // here. Kraken enforces the same thing; this only saves a rejected submit.
            if (this.unaccountedDrugs.length) {
                const drug = this.unaccountedDrugs[0];
                this.errorMessage = this.outstanding(drug) > 0
                    ? "Account for all " + drug.remaining + " of " + this.formatDrugName(drug.stock_item) + " between returned, given and destroyed. This check in closes the checkout."
                    : "More has been entered for " + this.formatDrugName(drug.stock_item) + " than was checked out.";
                return;
            }

            // No confirmation step for a partial return. Less coming back than went out is the norm
            // once anything has been given or destroyed, the row shows all four figures side by
            // side, and the amounts have to balance before we get here. Signing is the confirmation.

            this.realSubmit = {
                uuid: this.selectedCheckout.uuid,
                theatreCheckout: this.selectedCheckout,
                drugs: payloadDrugs,
                notes: this.notes,
                discard_method: this.hasDiscard ? this.discardMethod.trim() : null
            };

            const summaryDrugs = [];
            payloadDrugs.forEach(d => {
                if (d.checkin > 0) summaryDrugs.push({ drug: 'Returned Item', qty: d.checkin });
                if (d.administered > 0) summaryDrugs.push({ drug: 'Administered Item', qty: d.administered });
                if (d.discard > 0) summaryDrugs.push({ drug: 'Discarded Item', qty: d.discard });
            });

            this.saveContext = {
                type: 'TheatreCheckin',
                patient: this.selectedCheckout.patient ? this.selectedCheckout.patient.personal_information.firstname + ' ' + this.selectedCheckout.patient.personal_information.lastname : 'N/A',
                location: this.$store.state.currentLocation.name,
                drugs: summaryDrugs,
                doubleSig: this.requiresDoubleSig
            };

            this.errorMessage = '';
            this.displaySignatureModal = true;
        },
        isSuccessful(value) {
            if (value) {
                this.$emit('close', true, 'success');
            }
        }
    }
}
</script>

<style scoped>
</style>
